<template>
  <div class="chart-wrapper">
    <div class="d-flex justify-content-between align-items-center">
      <div
        v-if="title || showLabels"
        class="d-flex flex-column"
      >
        <h4
          v-if="title"
          class="font-weight-bold mb-2"
        >
          {{ title }}
        </h4>
        <div
          v-if="showLabels"
          class="flex-shrink-0 mr-3"
        >
          <ScrollShadowContainer
            max-height="170px"
            shadow-height="8px"
            shadow-color="rgba(248, 249, 250, 0.9)"
          >
            <div
              v-for="(item, index) in legendItems" 
              :key="index"
              class="small border-light"
              :class="index !== legendItems.length - 1 ? 'border-bottom' : ''"
            >
              <div class="d-flex align-items-center justify-content-between p-2">
                <div class="d-flex align-items-center flex-grow-1">
                  <div 
                    class="mr-2 flex-shrink-0 p-1 rounded" 
                    :style="{
                      backgroundColor: item.color,
                    }"
                  />
                  <span
                    class="text-truncate"
                    :title="item.label"
                  >{{ item.label }}</span>
                </div>
                <span class="ml-2 font-weight-bold flex-shrink-0">{{ item.percentage }}%</span>
              </div>
            </div>
          </ScrollShadowContainer>
        </div>
      </div>
      <div
        class="embed-responsive embed-responsive-1by1"
        :style="{'max-height': options?.maxHeight ?? '200px'}"
      >
        <canvas
          ref="canvas"
          class="embed-responsive-item"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { Doughnut, mixins } from 'vue-chartjs'
import ScrollShadowContainer from '@/components/ScrollShadowContainer.vue'

const endCapPlugin = {
  afterDraw: function(chart) {
    const ctx = chart.chart.ctx;
    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);
    const arc = meta.data[0]._view;

    if (!arc) return;

    const endAngle = arc.endAngle;
    const radius = (arc.outerRadius + arc.innerRadius) / 2;
    const thickness = (arc.outerRadius - arc.innerRadius) / 2;

    ctx.save();
    ctx.translate(arc.x, arc.y);

    // outer coloured circle
    ctx.fillStyle = dataset.backgroundColor[0];
    ctx.beginPath();
    ctx.arc(
      radius * Math.cos(endAngle),
      radius * Math.sin(endAngle),
      thickness * 1.5,
      0,
      2 * Math.PI
    );
    ctx.fill();

    // inner cut-out (white in middle)
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(
      radius * Math.cos(endAngle),
      radius * Math.sin(endAngle),
      thickness * 0.9,
      0,
      2 * Math.PI
    );
    ctx.fill();

    ctx.restore();
  }
};

export default {
  name: 'DoughnutChart',
  components: {
    ScrollShadowContainer
  },
  extends: Doughnut,
  mixins: [mixins.reactiveProp],
  props: {
    title: {
      type: String,
      required: false
    },
    showLabels: {
      type: Boolean,
      default: false
    },
    chartData: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    legendItems() {
      if (!this.chartData || !this.chartData.labels || !this.chartData.datasets || !this.chartData.datasets[0]) {
        return []
      }

      const labels = this.chartData.labels
      const data = this.chartData.datasets[0].data
      const baseColors = this.chartData.datasets[0].backgroundColor || ['#0B1A51', '#F26C0D', '#22D2C5', '#FF5A6E']

      return labels.map((label, index) => {
        const value = data[index] || 0
        const color = this.getCyclingColor(index, baseColors)
        
        return {
          label: label,
          value: value,
          percentage: typeof value === 'number' ? value.toFixed(1) : value,
          color: color
        }
      })
    },

    processedChartData() {
      if (!this.chartData || !this.chartData.datasets || !this.chartData.datasets[0]) {
        return this.chartData
      }

      const baseColors = this.chartData.datasets[0].backgroundColor || ['#0B1A51', '#F26C0D', '#22D2C5', '#FF5A6E']
      const dataLength = this.chartData.labels ? this.chartData.labels.length : 0
      
      // Generate colors for all data points by cycling through base colors
      const allColors = Array.from({ length: dataLength }, (_, index) => 
        this.getCyclingColor(index, baseColors)
      )

      return {
        ...this.chartData,
        datasets: [{
          ...this.chartData.datasets[0],
          backgroundColor: allColors
        }]
      }
    }
  },
  mounted () {
    const chartOptions = this.initChartOptions();

    if (chartOptions?.displayEndCap) {
      this.addPlugin(endCapPlugin);
    }

    this.renderChart(this.processedChartData, chartOptions)
  },
  methods: {
    getCyclingColor(index, colors) {
      return colors[index % colors.length]
    },
    initChartOptions() {
      const defaults = {
        maxHeight: '200px', // @TODO: Revisit this - chart sizing is difficult to restrain
        displayEndCap: false,
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 70,
        legend: { display: false },
        tooltips: { enabled: false },
        hover: { mode: null },
        layout: {
          padding: 6
        },
      }

      return {
        ...defaults,
        ...this.options
      }
    }
  }
}
</script>
